/**
 * Map service for Mapbox GL JS initialization and configuration
 * Provides utilities for map setup, layer management, and MapTiler integration
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
import mapboxgl from 'mapbox-gl';
import type { CoverageData } from '@/types';

// Use any for mapbox types due to version issues with type definitions
type MapInstance = any;

interface MapConfigOptions {
  center?: [number, number];
  zoom?: number;
  minZoom?: number;
  maxZoom?: number;
  style?: string;
  projection?: string;
  accessToken?: string;
}

interface FitBoundsOptions {
  padding?: number;
  maxZoom?: number;
  duration?: number;
}

/**
 * Default map configuration for Norwegian railway network
 */
export const DEFAULT_MAP_CONFIG: MapConfigOptions = {
  center: [10.7522, 59.9139] as [number, number], // Oslo, Norway
  zoom: 6,
  minZoom: 4,
  maxZoom: 18,
  style: 'mapbox://styles/mapbox/light-v11', // Default light style
  projection: 'mercator'
};

/**
 * MapTiler configuration
 * Note: VITE_MAPTILER_KEY should be set in .env file
 */
export const MAPTILER_CONFIG = {
  tilesUrl: 'https://api.maptiler.com/tiles/v3/{z}/{x}/{y}.pbf',
  attribution:
    '© <a href="https://www.maptiler.com/">MapTiler</a> © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap contributors</a>'
};

/**
 * Initialize Mapbox GL JS map instance
 * @param container - DOM element to render map into
 * @param options - Map configuration options
 * @returns Initialized map instance
 */
export function initializeMap(container: HTMLElement, options: MapConfigOptions = {}): MapInstance {
  if (!container) {
    throw new Error('Map container element is required');
  }

  // Set access token if provided
  if (options.accessToken) {
    (mapboxgl as any).accessToken = options.accessToken;
  }

  // Merge default config with provided options
  const config = {
    ...DEFAULT_MAP_CONFIG,
    ...options,
    container
  };

  // Create map instance
  const map = new (mapboxgl as any).Map(config);

  // Add navigation controls (zoom, rotate, pitch)
  const nav = new (mapboxgl as any).NavigationControl({
    showCompass: true,
    showZoom: true,
    visualizePitch: false
  });
  map.addControl(nav, 'top-right');

  // Add scale control
  const scale = new (mapboxgl as any).ScaleControl({
    maxWidth: 100,
    unit: 'metric'
  });
  map.addControl(scale, 'bottom-left');

  return map;
}

/**
 * Add GeoJSON source to map
 * @param map - Mapbox map instance
 * @param sourceId - Unique identifier for the source
 * @param data - GeoJSON FeatureCollection
 */
export function addGeoJSONSource(map: MapInstance, sourceId: string, data: CoverageData | null): void {
  if (!map || !sourceId) {
    console.error('Map and sourceId are required');
    return;
  }

  // Remove existing source if present
  if (map.getSource(sourceId)) {
    // Need to remove associated layers first
    const style = map.getStyle();
    const layers = style?.layers || [];
    layers.forEach((layer: any) => {
      if ('source' in layer && layer.source === sourceId) {
        map.removeLayer(layer.id);
      }
    });
    map.removeSource(sourceId);
  }

  map.addSource(sourceId, {
    type: 'geojson',
    data: (data as GeoJSON.FeatureCollection) || { type: 'FeatureCollection', features: [] }
  });
}

/**
 * Update GeoJSON source data
 * @param map - Mapbox map instance
 * @param sourceId - Source identifier
 * @param data - New GeoJSON FeatureCollection
 */
export function updateGeoJSONSource(map: MapInstance, sourceId: string, data: CoverageData): void {
  if (!map || !sourceId) return;

  const source = map.getSource(sourceId);
  if (source) {
    source.setData(data as GeoJSON.FeatureCollection);
  }
}

/**
 * Add operational points layer (circles)
 * @param map - Mapbox map instance
 * @param sourceId - Source identifier
 * @param layerId - Layer identifier
 */
export function addOperationalPointsLayer(map: MapInstance, sourceId: string, layerId: string): void {
  if (!map || map.getLayer(layerId)) return;

  map.addLayer({
    id: layerId,
    type: 'circle',
    source: sourceId,
    filter: ['==', ['get', 'type'], 'operationalPoint'],
    paint: {
      'circle-radius': [
        'interpolate',
        ['linear'],
        ['zoom'],
        6,
        4, // At zoom 6, radius 4px
        10,
        8, // At zoom 10, radius 8px
        14,
        12 // At zoom 14, radius 12px
      ],
      'circle-color': [
        'case',
        ['==', ['get', 'operationalType'], 'station'],
        '#0066cc', // Blue for stations
        ['==', ['get', 'operationalType'], 'stopPost'],
        '#009933', // Green for stop posts
        '#ff6600' // Orange for other types
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff',
      'circle-opacity': 0.8
    }
  });
}

/**
 * Add railway lines layer (lines/polygons)
 * @param map - Mapbox map instance
 * @param sourceId - Source identifier
 * @param layerId - Layer identifier
 */
export function addRailwayLinesLayer(map: MapInstance, sourceId: string, layerId: string): void {
  if (!map || map.getLayer(layerId)) return;

  // Add fill layer for polygons
  map.addLayer({
    id: `${layerId}-fill`,
    type: 'fill',
    source: sourceId,
    filter: [
      'all',
      ['==', ['get', 'type'], 'line'],
      ['==', ['geometry-type'], 'Polygon']
    ],
    paint: {
      'fill-color': '#6666cc',
      'fill-opacity': 0.3
    }
  });

  // Add line layer for polygon borders and linestrings
  map.addLayer({
    id: layerId,
    type: 'line',
    source: sourceId,
    filter: ['==', ['get', 'type'], 'line'],
    paint: {
      'line-color': '#6666cc',
      'line-width': [
        'interpolate',
        ['linear'],
        ['zoom'],
        6,
        2, // At zoom 6, width 2px
        10,
        3, // At zoom 10, width 3px
        14,
        4 // At zoom 14, width 4px
      ],
      'line-opacity': 0.8
    }
  });
}

/**
 * Add highlighted layer for selected features
 * @param map - Mapbox map instance
 * @param sourceId - Source identifier
 * @param layerId - Layer identifier
 */
export function addHighlightLayer(map: MapInstance, sourceId: string, layerId: string): void {
  if (!map || map.getLayer(layerId)) return;

  map.addLayer({
    id: layerId,
    type: 'line',
    source: sourceId,
    paint: {
      'line-color': '#ffff00',
      'line-width': 4,
      'line-opacity': 1
    },
    filter: ['==', ['get', 'id'], '']
  });
}

/**
 * Highlight feature by ID
 * @param map - Mapbox map instance
 * @param layerId - Highlight layer identifier
 * @param featureId - Feature ID to highlight
 */
export function highlightFeature(map: MapInstance, layerId: string, featureId: string): void {
  if (!map || !map.getLayer(layerId)) return;

  map.setFilter(layerId, ['==', ['get', 'id'], featureId || '']);
}

/**
 * Clear all highlights
 * @param map - Mapbox map instance
 * @param layerId - Highlight layer identifier
 */
export function clearHighlight(map: MapInstance, layerId: string): void {
  highlightFeature(map, layerId, '');
}

/**
 * Fit map to bounds
 * @param map - Mapbox map instance
 * @param bounds - Bounding box [minLon, minLat, maxLon, maxLat]
 * @param options - Fit bounds options
 */
export function fitToBounds(map: MapInstance, bounds: [number, number, number, number] | null, options: FitBoundsOptions = {}): void {
  if (!map || !bounds || bounds.length !== 4) return;

  const [minLon, minLat, maxLon, maxLat] = bounds;

  map.fitBounds(
    [
      [minLon, minLat],
      [maxLon, maxLat]
    ],
    {
      padding: 50,
      maxZoom: 12,
      duration: 1000,
      ...options
    }
  );
}

/**
 * Enable/disable map interaction
 * @param map - Mapbox map instance
 * @param enabled - Whether to enable interaction
 */
export function setMapInteraction(map: MapInstance, enabled: boolean): void {
  if (!map) return;

  if (enabled) {
    map.dragPan.enable();
    map.scrollZoom.enable();
    map.doubleClickZoom.enable();
    map.touchZoomRotate.enable();
  } else {
    map.dragPan.disable();
    map.scrollZoom.disable();
    map.doubleClickZoom.disable();
    map.touchZoomRotate.disable();
  }
}
